import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ColumnDef } from '@tanstack/react-table';
import api from '../../../services/api';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { TanTable } from '../../../components/TanTable';
import { useTryPermissions } from '../../../contexts/PermissionContext';
import { LcV1Save, LcV1SavesResponse, fmtMD } from './logic';

/**
 * Drill-down from the 360 Adoption chart: what one LC is still saving in V1
 * over the page's date range, one cms.history row per field changed (pro,
 * table, field, old/new value, when, V1's save type), newest first. Rows come
 * in pages (PAGE_SIZE) from /reporting/dispatcher-metrics?sections=v1_saves
 * and "Load more" appends, so a heavy V1 user's month (100k+ rows) never
 * arrives in one response; the header keeps the totals in view.
 */

export const PAGE_SIZE = 2000;

export interface V1SavesLc {
  key: string;   // dispatcher_key from the adoption rows
  name: string;
}

interface Props {
  adoptionGroup?: 'lcs' | 'invoice_clerks';
  open: boolean;
  lc: V1SavesLc | null;
  range: [string, string] | null;   // the page's date range, YYYY-MM-DD
  onClose: () => void;
  onProClick: (pro: number) => void;
}

type Row = LcV1Save & Record<string, any>;

// 'YYYY-MM-DD HH:MM:SS' -> 'M/D/YY HH:MM:SS' (string-wise, no timezone shift).
const fmtWhen = (s: string): string => {
  const [d, t] = (s || '').split(' ');
  if (!d) return s || '-';
  const [y, m, day] = d.split('-').map(Number);
  if (!y || !m || !day) return s;
  return `${m}/${day}/${String(y).slice(2)}${t ? ` ${t}` : ''}`;
};

const LcV1SavesModal: React.FC<Props> = ({ open, lc, range, onClose, onProClick, adoptionGroup = 'lcs' }) => {
  const perms = useTryPermissions();
  const canViewPro = perms ? perms.hasAccess('/pro/:pronumber') : true;

  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState<{ rows: number; saves: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const loadPage = useCallback(async (offset: number) => {
    if (!lc || !range) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/reporting/dispatcher-metrics', {
        params: {
          start_date: range[0],
          end_date: range[1],
          sections: 'v1_saves',
          adoption_group: adoptionGroup,
          include_lc: '1',
          background: 'true',   // the page's OTD request already records the run
          lc: lc.key,
          offset,
          limit: PAGE_SIZE,
        },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const payload: LcV1SavesResponse | undefined = res.data?.lc_v1_saves;
      const page = payload?.rows ?? [];
      setRows((prev) => (offset === 0 ? page : [...prev, ...page]));
      setTotals({ rows: payload?.total_rows ?? page.length, saves: payload?.total_saves ?? 0 });
    } catch (e: any) {
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') setError('Failed to load');
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [lc, range, adoptionGroup]);

  // Fresh list whenever the modal opens on an LC (or the range changed under it).
  useEffect(() => {
    if (!open || !lc || !range) return;
    setRows([]);
    setTotals(null);
    loadPage(0);
    return () => abortRef.current?.abort();
  }, [open, lc, range, loadPage]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const columns = useMemo<ColumnDef<Row>[]>(() => {
    const text = (key: string, header: string): ColumnDef<Row> => ({
      accessorKey: key,
      header,
      filterFn: 'arrIncludes',
      cell: ({ getValue }) => (getValue<string>() || '-'),
    });
    return [
      { accessorKey: 'datetime', header: 'Date/Time', filterFn: 'arrIncludes', cell: ({ getValue }) => fmtWhen(getValue<string>()) },
      {
        accessorKey: 'pro',
        header: 'Pro #',
        filterFn: 'arrIncludes',
        cell: ({ row }) => {
          const pro = row.original.pro;
          const n = Number(pro);
          if (!pro) return '-';
          if (!canViewPro || !Number.isFinite(n) || n <= 0) return <span className="text-body">{pro}</span>;
          return (
            <button
              type="button"
              onClick={() => onProClick(n)}
              className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
            >
              {pro}
            </button>
          );
        },
      },
      text('table', 'Table'),
      text('sub', 'Leg/Line'),
      text('field', 'Field'),
      text('old', 'Old Value'),
      text('new', 'New Value'),
      text('type', 'Type'),
    ];
  }, [canViewPro, onProClick]);

  const groupableColumns = useMemo(() => ['table', 'field', 'type', 'pro'], []);

  useAltHotkeys({ C: onClose }, undefined, { layer: open });
  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(open);

  if (!open || !lc) return null;

  const title = range ? `${lc.name} — V1 saves, ${fmtMD(range[0])}–${fmtMD(range[1])}` : `${lc.name} — V1 saves`;
  const more = totals ? Math.max(0, totals.rows - rows.length) : 0;

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-scrim/50 p-4" onClick={onClose}>
      <div
        ref={panelRef}
        style={panelStyle}
        className="relative flex flex-col w-full max-w-6xl h-[80vh] max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader
          title={title}
          onClose={onClose}
          dragHandleProps={dragHandleProps}
          className="flex-shrink-0"
          actions={
            totals ? (
              <span className="inline-flex items-center gap-3 text-xs text-sky-200 whitespace-nowrap">
                <span>
                  {rows.length.toLocaleString()} of {totals.rows.toLocaleString()} field changes
                  {' · '}{totals.saves.toLocaleString()} saves
                </span>
                {more > 0 && (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => loadPage(rows.length)}
                    className="rounded border border-sky-300/60 px-2 py-0.5 text-sky-100 hover:bg-sky-300/20 disabled:opacity-50"
                  >
                    {loading ? 'Loading…' : `Load ${Math.min(more, PAGE_SIZE).toLocaleString()} more`}
                  </button>
                )}
              </span>
            ) : undefined
          }
        />

        <div className="flex-1 min-h-0 p-3">
          {error ? (
            <div className="flex h-full items-center justify-center text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <TanTable
              data={rows}
              columns={columns}
              trackViewedColumnId="pro"
              loading={loading && rows.length === 0}
              searchPlaceholder="Search…"
              enableColumnFilters
              enableSearch
              enableGrouping
              groupableColumns={groupableColumns}
              enableShowHideCols
              enableExcelExport
              fillContainer
              disableVirtualization
              defaultPageSize={200}
              settingsKey="lc-metrics-v1-saves"
              disableSettingsPersistence
            />
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default LcV1SavesModal;
